import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogIn, Mail, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { authEmailLogin, authGoogleCallback } from '../services/api';
import { trackCompleteRegistration } from '../utils/metaPixel';

const ACCESS_TOKEN_KEY = 'oc_access_token';

const PortalLoginPage = () => {
  useDocumentMeta({ title: 'Bejelentkezés | Fiókom | Orosz Coaching' });
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);

  // If the user is returning from the Emergent Google Auth redirect, we
  // arrive at /fiokom/bejelentkezes#session_id=xxx. Consume the fragment,
  // exchange it server-side, and navigate to the dashboard.
  useEffect(() => {
    const hash = location.hash || window.location.hash;
    if (!hash || !hash.includes('session_id=')) return;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = decodeURIComponent(m[1]);
    setProcessingGoogle(true);
    // Immediately clear the fragment so a refresh doesn't retry.
    window.history.replaceState(null, '', location.pathname);
    (async () => {
      try {
        const res = await authGoogleCallback(sid);
        toast.success('Sikeres bejelentkezés Google-fiókkal.');
        // Only fire CompleteRegistration on the FIRST-EVER login (new user).
        if (res && res.is_new_user) {
          trackCompleteRegistration({ content_name: 'portal_google_signup', status: 'success' });
        }
        navigate('/fiokom', { replace: true });
      } catch (err) {
        setProcessingGoogle(false);
        toast.error(err?.response?.data?.detail || 'A Google bejelentkezés nem sikerült.');
      }
    })();
  }, [location, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/fiokom/bejelentkezes';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      const res = await authEmailLogin(email.trim(), password);
      localStorage.setItem(ACCESS_TOKEN_KEY, res.access_token);
      toast.success('Sikeres bejelentkezés.');
      navigate('/fiokom', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Hibás email vagy jelszó.');
    } finally {
      setSubmitting(false);
    }
  };

  if (processingGoogle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-yellow-600 mx-auto mb-3" size={32} />
          <p className="text-gray-600">Bejelentkeztetés…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16 md:py-24 bg-gradient-to-br from-white to-slate-100">
        <div className="w-full max-w-md" data-testid="portal-login-card">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-yellow-700 mb-3">
              <LogIn size={14} /> Fiókom
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Bejelentkezés
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              Nézd meg a bérleted, foglalásaid és lejárati idejét.
            </p>
          </div>

          <button
            data-testid="portal-login-google"
            onClick={handleGoogle}
            className="w-full h-14 flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-yellow-500 hover:shadow-md rounded-xl font-semibold text-gray-900 transition-all"
          >
            <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.72 1.22 9.22 3.62l6.86-6.86C35.9 2.06 30.29 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.45 13.5 17.7 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.5c0-1.57-.15-3.09-.42-4.5H24v9h12.94c-.58 3.13-2.29 5.79-4.87 7.58l7.73 6c4.51-4.16 7.18-10.29 7.18-18.08z"/>
              <path fill="#FBBC05" d="M10.54 28.59a14.52 14.52 0 0 1 0-9.18l-7.98-6.19a24.02 24.02 0 0 0 0 21.56l7.98-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.14 15.9-5.83l-7.73-6c-2.14 1.44-4.9 2.28-8.17 2.28-6.3 0-11.64-4.24-13.55-9.94l-7.98 6.19C6.49 42.62 14.61 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            <span className="text-base">Bejelentkezés Google-fiókkal</span>
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-gray-400">
            <span className="flex-1 h-px bg-gray-200" />
            vagy email-lel
            <span className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="portal-login-form">
            <div>
              <Label htmlFor="pl-email" className="text-gray-700 text-xs uppercase tracking-widest">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  data-testid="portal-login-email"
                  id="pl-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="te@email.hu"
                  className="pl-10 h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pl-password" className="text-gray-700 text-xs uppercase tracking-widest">Jelszó</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  data-testid="portal-login-password"
                  id="pl-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
            </div>
            <Button
              data-testid="portal-login-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-gray-900 hover:bg-yellow-600 text-white font-bold transition-colors"
            >
              {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Belépés…</> : 'Belépés'}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-6">
            Még nincs fiókod? A bérlet megvásárlása után kapsz egy emailt a jelszó beállításához.<br/>
            <Link to="/#programs" className="text-yellow-700 underline hover:text-yellow-600">Vissza a programokhoz</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PortalLoginPage;
